import React, { KeyboardEvent, useCallback, useState } from 'react'
import { useTheme } from 'styled-components'
import { Swiper as SwiperType } from 'swiper'
import { Thumbs } from 'swiper/modules'

import { Carat, RotateClockwise, useIsBrowser } from '@syconium/little-miss-figgy'

import {
  GalleryAsset,
  GalleryImageStateContainer,
} from '../../../../app/_components/pages/product-pages/shared/providers/GalleryImageStateContainer'
import {
  SwiperSlideWithAnalytics,
  SwiperWithAnalytics,
} from '../../../../app/_components/swiper/SwiperWithAnalytics'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { Thumbnails } from '../IProps'

import * as Styled from './styles'
// @ts-ignore

const maxThumbnailsVisibleAtATime = 8
const swiperModules = [Thumbs]

export const ThumbNailsDisplay: React.FC<Thumbnails> = ({
  gallerySwiperRef,
  onThumbsSwiperInit,
}) => {
  const {
    pages: {
      product: { gallery: strings },
    },
  } = useTranslation()

  const isBrowser = useIsBrowser()
  const theme = useTheme()
  const { allAssets, imageDescription } = GalleryImageStateContainer.useContainer()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>()
  const onThumbsSwiperInitCallback = useCallback(
    (swiper: SwiperType) => {
      setThumbsSwiper(swiper)
      onThumbsSwiperInit?.(swiper)
    },
    [onThumbsSwiperInit]
  )

  const getThumbnail = (slide: GalleryAsset, index: number) => {
    switch (slide.__typename) {
      case 'ProductCatalogImage':
      case 'KitColoredImage':
        return (
          <Styled.ThumbnailImage
            alt={slide.altText || imageDescription || ''}
            src={slide.url}
            widths={{ unit: 'px', sm: 100, md: 100 }}
            aspectRatios={{
              sm: 1,
              md: 1,
            }}
            loading={index > 0 ? 'lazy' : 'default'}
            fetchPriority={index > 0 ? 'low' : 'auto'}
          />
        )
      case 'ProductCatalogVideo':
        return (
          <Styled.ThumbnailVideo
            ariaLabel={imageDescription || strings.thumbnailVideoAriaLabel}
            loading='defer'
            autoPlay
            playsInline
            muted
            loop
            src={slide.url}
            type={'video/mp4'}
          />
        )
      case 'CustomSlide':
        return slide.children
      default:
        return null
    }
  }

  const shouldDisplayPagination = allAssets.length > maxThumbnailsVisibleAtATime

  const handleSlideBackwardClick = () => {
    thumbsSwiper?.slidePrev(undefined, false)
  }

  const handleSlideForwardClick = () => {
    thumbsSwiper?.slideNext(undefined, false)
  }

  const handleThumbnailKeyPress = (event: KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      gallerySwiperRef.current?.slideTo(index, undefined, false)
    }
  }

  return (
    <Styled.ThumbnailSwiperWrapper>
      {shouldDisplayPagination && (
        <Styled.ThumbnailSwiperNavButton onClick={handleSlideBackwardClick} $alignItems='start'>
          <Carat
            rotateClockwise={RotateClockwise.ThreeQuarters}
            stroke={theme.color.deprecated.obsidianLighter}
          />
        </Styled.ThumbnailSwiperNavButton>
      )}

      <SwiperWithAnalytics
        className='product-images-slider-thumbs'
        direction='vertical'
        modules={swiperModules}
        onInit={onThumbsSwiperInitCallback}
        slidesPerView={maxThumbnailsVisibleAtATime}
      >
        {allAssets.map((slide, index: number) => {
          if (!isBrowser && index >= maxThumbnailsVisibleAtATime) return null
          return (
            <SwiperSlideWithAnalytics
              key={`slide-${'url' in slide ? slide.url : ''}-${index}`}
              aria-label={`${strings.viewImage} ${index}`}
              tabIndex={0}
              role='button'
              onKeyDown={event => handleThumbnailKeyPress(event, index)}
            >
              {getThumbnail(slide, index)}
            </SwiperSlideWithAnalytics>
          )
        })}
      </SwiperWithAnalytics>
      {shouldDisplayPagination && (
        <Styled.ThumbnailSwiperNavButton onClick={handleSlideForwardClick} $alignItems='end'>
          <Carat
            rotateClockwise={RotateClockwise.Quarter}
            stroke={theme.color.deprecated.obsidianLighter}
          />
        </Styled.ThumbnailSwiperNavButton>
      )}
    </Styled.ThumbnailSwiperWrapper>
  )
}
